package cc.bluepeak.clubmanager.site.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import cc.bluepeak.clubmanager.site.Res
import cc.bluepeak.clubmanager.site.components.widgets.IconButton
import cc.bluepeak.clubmanager.site.styles.Theme
import cc.bluepeak.clubmanager.site.styles.toSitePalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onAnimationEnd
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.lucide.LucideMenu
import com.varabyte.kobweb.silk.components.icons.lucide.LucideX
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.defer.Deferred
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.AnimationDirection
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.Div

val MobileMenuStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.Block).fillMaxWidth()
    }
    Breakpoint.MD {
        Modifier.display(DisplayStyle.None)
    }
}

val DesktopMenuStyle = CssStyle {
    base {
        Modifier.display(DisplayStyle.None)
    }
    Breakpoint.MD {
        Modifier.display(DisplayStyle.Block)
    }
}

val MobileMenuAnimations = Keyframes {
    from {
        Modifier.opacity(0)
    }
    to {
        Modifier.opacity(1)
    }
}

val NavHeaderStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().borderRadius(Theme.shapes.medium)
            .backgroundColor(colorMode.toSitePalette().surface)
    }
}

val NavHeaderWrapperStyle = CssStyle {
    base {
        Modifier.fillMaxWidth(100.percent).backgroundColor(colorMode.toSitePalette().background)
            .borderRadius(bottomLeft = Theme.shapes.medium, bottomRight = Theme.shapes.medium)
            .margin(leftRight = 1.cssRem)
    }
    Breakpoint.SM {
        Modifier.fillMaxWidth(80.percent).margin(leftRight = 0.px)
    }
}

enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

val NavHeaderLinkStyle = CssStyle {
    base {
        Modifier
    }
    Breakpoint.SM {
        Modifier.alignItems(AlignItems.End)
    }
}

@Composable
fun NavHeader(
    menuItems: @Composable (() -> Unit) -> Unit,
    ctaItems: @Composable () -> Unit,
) {
    Deferred {
        Row(
            Modifier.fillMaxWidth().position(Position.Fixed).zIndex(999999),
            horizontalArrangement = Arrangement.Center
        ) {
            Box(NavHeaderWrapperStyle.toModifier(), contentAlignment = Alignment.Center) {
                Row(
                    NavHeaderStyle.toModifier().padding(.8.cssRem).margin(top = 1.cssRem),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Link(
                        Res.link.home,
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(1.cssRem),
                            modifier = NavHeaderLinkStyle.toModifier()
                        ) {
                            Image(
                                Res.image.logo,
                                alt = "ClubManager",
                                modifier = Modifier.height(clamp(1.cssRem, 7.vw, 2.cssRem))
                            )
                            SpanText(Res.string.clubmanager, Modifier.fontWeight(500).fontSize(1.2.cssRem))
                        }
                    }

                    Div(DesktopMenuStyle.toAttrs()) {
                        Row(
                            Modifier.fillMaxWidth().gap(1.5.cssRem).fontSize(.9.cssRem).fontWeight(FontWeight.Bold),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            menuItems {}
                        }
                    }

                    Row(Modifier.displayIfAtLeast(Breakpoint.MD), verticalAlignment = Alignment.CenterVertically) {
                        ctaItems()
                    }

                    Div(MobileMenuStyle.toAttrs()) {
                        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                            var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }
                            Spacer()
                            IconButton({
                                menuState = if (menuState == SideMenuState.CLOSED) SideMenuState.OPEN
                                else menuState.close()
                            }, Modifier.backgroundColor(Colors.Transparent).color(Theme.primary)) {
                                if (menuState == SideMenuState.CLOSED) LucideMenu()
                                else LucideX()
                            }
                            if (menuState != SideMenuState.CLOSED) {
                                MobileMenu(
                                    menuItems,
                                    menuState,
                                    onAnimationEnd = {
                                        if (menuState == SideMenuState.CLOSING) menuState = SideMenuState.CLOSED
                                    },
                                    close = { menuState = menuState.close() },
                                    onCancel = { menuState = SideMenuState.CLOSED }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

val MobileMenuOverlayStyle = CssStyle {
    base {
        Modifier
            .zIndex(999999)
            .margin(top = 5.cssRem)
            .backgroundColor(colorMode.toSitePalette().background)
            .padding(1.cssRem)
            .setVariable(OverlayVars.BackgroundColor, Colors.Transparent)

    }
}

val MobileMenuBoxStyle = CssStyle {
    base {
        Modifier.fillMaxSize()
            .padding(top = 1.cssRem)
            .gap(1.5.cssRem)
            .backgroundColor(colorMode.toSitePalette().surface)
            .borderRadius(Theme.shapes.medium)
    }
}

@Composable
private fun MobileMenu(
    menuItems: @Composable (() -> Unit) -> Unit,
    menuState: SideMenuState,
    onAnimationEnd: () -> Unit,
    close: () -> Unit,
    onCancel: () -> Unit,
) {
    Overlay(MobileMenuOverlayStyle.toModifier()) {
        key(menuState) {
            Column(
                MobileMenuBoxStyle.toModifier()
                    .animation(
                        MobileMenuAnimations.toAnimation(
                            100.ms,
                            timingFunction = if (menuState == SideMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn,
                            direction = if (menuState == SideMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse,
                            fillMode = AnimationFillMode.Forwards
                        )
                    )
                    .onClick {
                        close()
                        it.stopPropagation()
                    }
                    .onAnimationEnd { onAnimationEnd() },
                verticalArrangement = Arrangement.Top,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                menuItems { onCancel() }
            }
        }
    }
}